import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import { TradingMode } from '../App';

import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from '../constants/tradingPairs';

interface SearchablePairSelectorProps {
  selectedPair: string;
  onPairSelect: (pair: string) => void;
  tradingMode: 'spot' | 'futures' | 'cfd' | 'binary' | 'robot' | 'wallet' | 'events';
  marketDataList?: any[]; // Not used anymore as we're using predefined pairs
}

const SearchablePairSelector: React.FC<SearchablePairSelectorProps> = ({
  selectedPair,
  onPairSelect,
  tradingMode,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Get filtered tickers based on trading mode, search, and category
  const getFilteredTickers = useCallback(() => {
    // Use the appropriate predefined list based on trading mode
    const baseList = tradingMode === 'cfd' ? CFD_INSTRUMENTS : TOP_CRYPTO_PAIRS;
    
    // Filter by search term if provided
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      return baseList.filter(ticker => 
        ticker.symbol.toLowerCase().includes(term) ||
        ticker.name.toLowerCase().includes(term)
      );
    }
    
    return baseList;
  }, [tradingMode, searchTerm]);

  const handlePairSelect = (symbol: string) => {
    onPairSelect(symbol);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Updated formatPairDisplay function to handle CFD instruments correctly
  const formatPairDisplay = (symbol: string, name?: string) => {
    if (tradingMode === 'cfd') {
      // For CFD instruments - display with slashes for UI only
      if (symbol.includes('USD') && !symbol.includes('USDT')) {
        // For forex pairs, format as XXX/YYY
        if (symbol.length === 6) {
          return `${symbol.substring(0, 3)}/${symbol.substring(3, 6)}`;
        }
        
        // For commodities, use their display names
        if (symbol === 'XAUUSD') return 'Gold/USD';
        if (symbol === 'XAGUSD') return 'Silver/USD';
        if (symbol === 'XPTUSD') return 'Platinum/USD';
        if (symbol === 'WTIUSD') return 'Oil/USD';
      }
      // Stock symbols remain as is
      return name ? `${symbol}` : symbol;
    }
    
    // For crypto pairs, format as BTC/USDT
    if (symbol.endsWith('USDT')) {
      return symbol.replace('USDT', '/USDT');
    }
    return symbol;
  };

  const getCategoryIcon = (type: 'forex' | 'commodity' | 'stock' | 'crypto' | 'index') => {
    switch (type) {
      case 'forex': return '💱';
      case 'commodity': return '🥇';
      case 'stock': return '📈';
      case 'crypto': return '₿';
      case 'index': return '📊';
      default: return '📊';
    }
  };

  const filteredTickers = getFilteredTickers();

  // Get current pair display name
  const currentPairDisplay = formatPairDisplay(selectedPair);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 hover:bg-slate-800 px-3 py-2 rounded-lg transition-colors"
      >
        <h1 className="text-2xl font-bold text-white">
          {currentPairDisplay}
          {tradingMode === 'futures' && <span className="text-sm text-slate-400 ml-2">Perp</span>}
          {tradingMode === 'cfd' && <span className="text-sm text-slate-400 ml-2">CFD</span>}
        </h1>
        <ChevronDown size={16} className="text-slate-400" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 w-96 max-h-96 overflow-hidden">
          {/* Search Header */}
          <div className="p-4 border-b border-slate-700">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder={`Search ${tradingMode === 'cfd' ? 'instruments' : 'pairs'}...`}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full bg-slate-900 text-white pl-10 pr-10 py-2 rounded-xl border border-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto">
            {filteredTickers.length > 0 ? (
              <div className="p-2">
                {/* Group by type for CFD mode */}
                {tradingMode === 'cfd' && (
                  <>
                    {/* Forex Section */}
                    <div className="text-xs font-semibold text-slate-400 uppercase mt-2 mb-1 px-3">
                      Forex Pairs
                    </div>
                    {filteredTickers
                      .filter(ticker => ticker.type === 'forex')
                      .map((ticker) => (
                        <button
                          key={ticker.symbol}
                          onClick={() => handlePairSelect(ticker.symbol)}
                          className={`w-full text-left px-3 py-2 rounded hover:bg-slate-700 transition-colors flex items-center justify-between ${
                            selectedPair === ticker.symbol ? 'bg-slate-700 text-blue-400' : 'text-white'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{getCategoryIcon(ticker.type)}</span>
                            <div>
                              <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name)}</div>
                              <div className="text-xs text-slate-400 truncate max-w-48">{ticker.name}</div>
                            </div>
                          </div>
                          <span className="text-xs text-slate-500 uppercase">{ticker.type}</span>
                        </button>
                      ))}
                    
                    {/* Commodities Section */}
                    <div className="text-xs font-semibold text-slate-400 uppercase mt-4 mb-1 px-3">
                      Commodities
                    </div>
                    {filteredTickers
                      .filter(ticker => ticker.type === 'commodity')
                      .map((ticker) => (
                        <button
                          key={ticker.symbol}
                          onClick={() => handlePairSelect(ticker.symbol)}
                          className={`w-full text-left px-3 py-2 rounded hover:bg-slate-700 transition-colors flex items-center justify-between ${
                            selectedPair === ticker.symbol ? 'bg-slate-700 text-blue-400' : 'text-white'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{getCategoryIcon(ticker.type)}</span>
                            <div>
                              <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name)}</div>
                              <div className="text-xs text-slate-400 truncate max-w-48">{ticker.name}</div>
                            </div>
                          </div>
                          <span className="text-xs text-slate-500 uppercase">{ticker.type}</span>
                        </button>
                      ))}
                    
                    {/* Stocks Section */}
                    <div className="text-xs font-semibold text-slate-400 uppercase mt-4 mb-1 px-3">
                      Stocks
                    </div>
                    {filteredTickers
                      .filter(ticker => ticker.type === 'stock')
                      .map((ticker) => (
                        <button
                          key={ticker.symbol}
                          onClick={() => handlePairSelect(ticker.symbol)}
                          className={`w-full text-left px-3 py-2 rounded hover:bg-slate-700 transition-colors flex items-center justify-between ${
                            selectedPair === ticker.symbol ? 'bg-slate-700 text-blue-400' : 'text-white'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{getCategoryIcon(ticker.type)}</span>
                            <div>
                              <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name)}</div>
                              <div className="text-xs text-slate-400 truncate max-w-48">{ticker.name}</div>
                            </div>
                          </div>
                          <span className="text-xs text-slate-500 uppercase">{ticker.type}</span>
                        </button>
                      ))}
                  </>
                )}
                
                {/* Non-CFD mode - just list all pairs */}
                {tradingMode !== 'cfd' && filteredTickers.map((ticker) => (
                  <button
                    key={ticker.symbol}
                    onClick={() => handlePairSelect(ticker.symbol)}
                    className={`w-full text-left px-3 py-2 rounded hover:bg-slate-700 transition-colors flex items-center justify-between ${
                      selectedPair === ticker.symbol ? 'bg-slate-700 text-blue-400' : 'text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{getCategoryIcon(ticker.type)}</span>
                      <div>
                        <div className="font-medium">{formatPairDisplay(ticker.symbol, ticker.name)}</div>
                        <div className="text-xs text-slate-400 truncate max-w-48">{ticker.name}</div>
                      </div>
                    </div>
                    <span className="text-xs text-slate-500 uppercase">{ticker.type}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-slate-400">
                {searchTerm ? `No ${tradingMode === 'cfd' ? 'instruments' : 'pairs'} found` : `No ${tradingMode === 'cfd' ? 'instruments' : 'pairs'} available`}
              </div>
            )}
          </div>

          {/* Footer info */}
          <div className="p-3 border-t border-slate-700 bg-slate-900/50">
            <div className="text-xs text-slate-400 text-center">
              {filteredTickers.length} {tradingMode === 'cfd' ? 'instruments' : 'pairs'} available
              {searchTerm && ` • Filtered by "${searchTerm}"`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchablePairSelector;